import * as THREE from 'three';
import { Spin } from '../animated';
import { CLASSICAL, INDUSTRIAL } from '../materials';
import { Beam, gableRoof, HexPad, levelEntry, type ModelProps, Pole } from '../parts';

const { STEEL, IRON, SOOT, BRICK, DARK_BRICK, SLATE, ORE, SPOIL, RAIL, SLEEPER } = INDUSTRIAL;
const { DARK_WOOD, SOIL } = CLASSICAL;

const G = {
  collar: new THREE.BoxGeometry(0.2, 0.03, 0.2),
  beam: new THREE.BoxGeometry(0.22, 0.03, 0.05),
  wheelRim: new THREE.TorusGeometry(0.085, 0.012, 4, 16),
  spoke: new THREE.BoxGeometry(0.16, 0.012, 0.012),
  hub: new THREE.CylinderGeometry(0.02, 0.02, 0.05, 8).rotateX(Math.PI / 2),
  band: new THREE.BoxGeometry(0.2, 0.02, 0.02),
  shed: new THREE.BoxGeometry(0.22, 0.13, 0.18),
  shedRoof: gableRoof(0.11, 0.25),
  door: new THREE.BoxGeometry(0.07, 0.09, 0.01),
  engineHouse: new THREE.BoxGeometry(0.18, 0.16, 0.16),
  engineRoof: gableRoof(0.1, 0.2),
  chimney: new THREE.CylinderGeometry(0.022, 0.028, 0.3, 6),
  rail: new THREE.BoxGeometry(0.012, 0.012, 1),
  sleeper: new THREE.BoxGeometry(0.13, 0.01, 0.025),
  cart: new THREE.BoxGeometry(0.1, 0.06, 0.12),
  cartWheel: new THREE.CylinderGeometry(0.018, 0.018, 0.012, 8).rotateZ(Math.PI / 2),
  oreLoad: new THREE.DodecahedronGeometry(0.05, 0),
  heap: new THREE.ConeGeometry(0.16, 0.16, 7),
  hopper: new THREE.CylinderGeometry(0.1, 0.04, 0.12, 4).rotateY(Math.PI / 4),
  lamp: new THREE.BoxGeometry(0.06, 0.03, 0.03),
};

// The headframe stands over the shaft at the back; the track runs from it to the front.
const SHAFT = { x: 0, z: -0.24 };
const FRAME_HEIGHT = 0.46;
const WHEEL_SPEED = 1.6; // radians per second
const TRACK = { x: 0, from: -0.12, to: 0.56 };
const SLEEPERS = Array.from({ length: 10 }, (_, i) => TRACK.from + 0.035 + i * 0.07);
// Cart spots along the track, nearest the shaft first.
const CART_SPOTS = [0.02, 0.2, 0.38];

type MineLook = {
  /** Headframe height scale. */
  frame: number;
  /** Ore carts on the track. */
  carts: number;
  /** Spoil heap scale (front right). */
  heap: number;
  /** A brick shed (else timber), and a brick engine house with a chimney, cabled to the wheel. */
  brick: boolean;
  /** Team bands on the headframe, a floodlight, and an ore hopper fed by a conveyor. */
  works: boolean;
};

/** Mine look by level: entry i is level i + 1, levels past the end use the last. */
const MINE_LEVELS: readonly MineLook[] = [
  { frame: 1, carts: 1, heap: 0.8, brick: false, works: false },
  { frame: 1.15, carts: 2, heap: 1, brick: true, works: false },
  { frame: 1.3, carts: 3, heap: 1.2, brick: true, works: true },
];

/** Two rails on sleepers along z, from `from` to `to`, at `x`. */
function Track({ x, from, to }: { x: number; from: number; to: number }) {
  const length = to - from;
  const sleepers = SLEEPERS.filter((z) => z < to);
  return (
    <group position={[x, 0, 0]}>
      {sleepers.map((z) => (
        <mesh key={z} geometry={G.sleeper} material={SLEEPER} position={[0, 0.005, z]} />
      ))}
      {[-0.04, 0.04].map((rx) => (
        <mesh
          key={rx}
          geometry={G.rail}
          material={RAIL}
          position={[rx, 0.016, from + length / 2]}
          scale={[1, 1, length]}
        />
      ))}
    </group>
  );
}

/** An iron ore cart heaped with ore, on the track at `z`. */
function OreCart({ x, z }: { x: number; z: number }) {
  return (
    <group position={[x, 0, z]}>
      <mesh geometry={G.cart} material={IRON} position={[0, 0.058, 0]} />
      <mesh geometry={G.oreLoad} material={ORE} position={[0, 0.09, 0]} scale={[0.9, 0.6, 1]} />
      {[-0.052, 0.052].flatMap((wx) =>
        [-0.035, 0.035].map((wz) => (
          <mesh
            key={`${wx}:${wz}`}
            geometry={G.cartWheel}
            material={SOOT}
            position={[wx, 0.02, wz]}
          />
        ))
      )}
    </group>
  );
}

/**
 * The headframe over the shaft: two steel legs braced from behind, a cross
 * beam and a winding wheel facing the camera (it turns).
 */
function Headframe({
  accent,
  scale,
  bands,
}: {
  accent: THREE.Material;
  scale: number;
  bands: boolean;
}) {
  const h = FRAME_HEIGHT;
  return (
    <group position={[SHAFT.x, 0, SHAFT.z]}>
      <mesh geometry={G.collar} material={SOOT} position={[0, 0.015, 0]} />
      <group scale={[1, scale, 1]}>
        {[-0.08, 0.08].map((x) => (
          <group key={x}>
            <Beam from={[x, 0, 0.03]} to={[x, h, 0.03]} material={STEEL} />
            {/* Back-stay from the ground behind up to the top. */}
            <Beam from={[x, 0, -0.2]} to={[x, h, 0.01]} material={STEEL} thickness={0.016} />
          </group>
        ))}
        <mesh geometry={G.beam} material={STEEL} position={[0, h, -0.01]} />
        {bands &&
          [0.16, 0.32].map((y) => (
            <mesh key={y} geometry={G.band} material={accent} position={[0, y, 0.03]} />
          ))}
      </group>
      <Spin axis="z" speed={-WHEEL_SPEED} position={[0, h * scale + 0.07, 0.03]}>
        <mesh geometry={G.wheelRim} material={IRON} />
        <mesh geometry={G.spoke} material={IRON} />
        <mesh geometry={G.spoke} material={IRON} rotation={[0, 0, Math.PI / 2]} />
        <mesh geometry={G.hub} material={SOOT} />
      </Spin>
    </group>
  );
}

/**
 * A mine: a steel headframe with a turning winding wheel over the shaft, ore
 * carts on a track running to the front, a shed with a team roof and a spoil
 * heap. L2 builds in brick and adds an engine house cabled to the wheel; L3
 * bands the headframe in team colour and adds a floodlight and an ore hopper.
 */
export function Mine({ accent, glow, level }: ModelProps) {
  const look = levelEntry(MINE_LEVELS, level);
  const wheelTop = FRAME_HEIGHT * look.frame + 0.07;
  return (
    <>
      <Headframe accent={accent} scale={look.frame} bands={look.works} />
      <Track {...TRACK} />
      {CART_SPOTS.slice(0, look.carts).map((z) => (
        <OreCart key={z} x={TRACK.x} z={z} />
      ))}
      <group position={[-0.36, 0, -0.04]}>
        <mesh
          geometry={G.shed}
          material={look.brick ? BRICK : DARK_WOOD}
          position={[0, 0.065, 0]}
        />
        <mesh geometry={G.door} material={SOOT} position={[0.04, 0.045, 0.091]} />
        <mesh geometry={G.shedRoof} material={accent} position={[0, 0.17, 0]} />
      </group>
      <mesh
        geometry={G.heap}
        material={SPOIL}
        position={[0.34, 0.08 * look.heap, 0.3]}
        scale={look.heap}
      />
      {look.brick && (
        <>
          <group position={[0.36, 0, -0.26]}>
            <mesh geometry={G.engineHouse} material={DARK_BRICK} position={[0, 0.08, 0]} />
            <mesh geometry={G.engineRoof} material={SLATE} position={[0, 0.2, 0]} />
            <mesh geometry={G.chimney} material={DARK_BRICK} position={[0.06, 0.15, -0.05]} />
          </group>
          {/* The winding cable, from the wheel's rim down to the engine house roof. */}
          <Beam
            from={[0.085, wheelTop, SHAFT.z + 0.03]}
            to={[0.3, 0.2, -0.26]}
            material={SOOT}
            thickness={0.008}
          />
        </>
      )}
      {look.works && (
        <>
          <group position={[-0.24, 0, 0.36]}>
            {[-0.06, 0.06].map((x) => (
              <Pole key={x} length={0.16} position={[x, 0.08, 0]} material={STEEL} />
            ))}
            <mesh geometry={G.hopper} material={IRON} position={[0, 0.2, 0]} />
          </group>
          {/* The conveyor from the shaft up to the hopper. */}
          <Beam
            from={[-0.1, 0.03, SHAFT.z + 0.1]}
            to={[-0.22, 0.26, 0.3]}
            material={SOOT}
            thickness={0.04}
          />
          <group position={[-0.44, 0, -0.34]}>
            <Pole length={0.5} position={[0, 0.25, 0]} material={STEEL} />
            <mesh geometry={G.lamp} material={glow} position={[0.03, 0.5, 0]} />
          </group>
        </>
      )}
    </>
  );
}

// Extra tile: spoil heaps, and a track across the tile with carts on it.
type MineTileLook = {
  heaps: { x: number; z: number; s: number }[];
  carts: number[];
  /** A second track, a lamp post and ore bins with team rims. */
  busy: boolean;
};

const TILE_HEAPS = [
  { x: -0.3, z: -0.3, s: 1.2 },
  { x: 0.3, z: 0.28, s: 1 },
];
const MORE_TILE_HEAPS = [
  { x: -0.3, z: -0.3, s: 1.5 },
  { x: 0.3, z: 0.28, s: 1.3 },
  { x: 0.36, z: -0.28, s: 1 },
  { x: -0.38, z: 0.2, s: 0.9 },
];
// Extra-tile look by level (tiles exist from L2; L1 is kept so the table indexes by level).
const MINE_TILE_LEVELS: readonly MineTileLook[] = [
  { heaps: TILE_HEAPS, carts: [], busy: false },
  { heaps: TILE_HEAPS, carts: [0.1], busy: false },
  { heaps: MORE_TILE_HEAPS, carts: [-0.2, 0.25], busy: true },
];
const TILE_TRACK = { from: -0.56, to: 0.56 };
// Ore bins at the front, clear of both tracks and the heaps.
const BINS = [
  { x: -0.12, z: 0.46 },
  { x: -0.25, z: 0.4 },
];

/** A grown mine's extra tile: spoil heaps either side of a track, busier at L3. */
export function MineTile({ accent, glow, level }: ModelProps) {
  const look = levelEntry(MINE_TILE_LEVELS, level);
  return (
    <>
      <HexPad material={SOIL} />
      {look.heaps.map((h) => (
        <mesh
          key={`${h.x}:${h.z}`}
          geometry={G.heap}
          material={SPOIL}
          position={[h.x, 0.08 * h.s, h.z]}
          scale={h.s}
        />
      ))}
      <group position={[0, 0.03, 0]}>
        <Track x={0} {...TILE_TRACK} />
        {look.carts.map((z) => (
          <OreCart key={z} x={0} z={z} />
        ))}
        {look.busy && (
          <>
            <group rotation={[0, Math.PI / 3, 0]}>
              <Track x={0} {...TILE_TRACK} />
            </group>
            {BINS.map((b) => (
              <group key={b.x} position={[b.x, 0, b.z]} rotation={[0, 0.4, 0]}>
                <mesh geometry={G.cart} material={accent} position={[0, 0.03, 0]} />
                <mesh
                  geometry={G.oreLoad}
                  material={ORE}
                  position={[0, 0.07, 0]}
                  scale={[0.9, 0.6, 1]}
                />
              </group>
            ))}
            <group position={[0.18, 0, -0.46]}>
              <Pole length={0.4} position={[0, 0.2, 0]} material={STEEL} />
              <mesh geometry={G.lamp} material={glow} position={[0.03, 0.4, 0]} />
            </group>
          </>
        )}
      </group>
    </>
  );
}
